<template>
  <div class="table-wrapper">
    <div class="table-content">
      <div
        v-if="title"
        class="table-title"
      >
        {{ title }}
      </div>
      <el-table
        ref="multipleTable"
        :row-key="rowKey"
        :default-sort="sorting"
        :data="tableRows"
        :height="height"
        :cell-class-name="tableCellClassName"
        :row-class-name="customRowClass"
        border
        @sort-change="sortChange"
      >
        <el-table-column
          label=""
          align="center"
          :width="50"
        >
        </el-table-column>
        <el-table-column
          v-for="(column, index) in filteredColumns"
          :key="index"
          :prop="index"
          :label="$t(`filters.${column.label}`)"
          :sortable="column.sortable"
          :align="column.align"
          :min-width="column.minWidth ? column.minWidth : ''"
          :fixed="column.fixed ? 'right' : false"
        >
          <template slot-scope="scope">
            <span v-if="column.type === 'text'">{{
              propertyValue(scope.row, column.key)
            }}</span>
            <span v-if="column.type === 'translatedText'">
              {{ $t(`filters.${propertyValue(scope.row, column.key)}`) }}
            </span>
            <span v-if="column.type === 'boolean'">
              <span
                :style="{ cursor: column.emit ? 'pointer' : 'auto' }"
                @click="
                  () => {
                    if (column.emit) $emit(column.emit, scope.row);
                  }
                "
              >
                <i
                  v-if="scope.row[index]"
                  class="state-icon fas fa-check-circle"
                />
                <i
                  v-else
                  class="state-icon fas fa-do-not-enter"
                />
              </span>
            </span>
            <span v-if="column.type === 'date'">
              <span v-if="propertyValue(scope.row, column.key)">
                {{
                  propertyValue(scope.row, column.key) | date('DD MMMM YYYY')
                }}
              </span>
            </span>
            <span v-if="column.type === 'datetime'">
              <span v-if="propertyValue(scope.row, column.key)">
                {{
                  propertyValue(scope.row, column.key)
                    | datetime('DD MMMM YYYY HH:mm')
                }}
              </span>
            </span>
            <span v-if="column.type === 'enum'">
              <span v-if="propertyValue(scope.row, column.key)">
                {{
                  $t(
                    `enums.${column.enum}.${propertyValue(
                      scope.row,
                      column.key
                    )}`
                  )
                }}
              </span>
            </span>
            <span v-if="column.type === 'encoded'">
              <span v-if="propertyValue(scope.row, column.key) !== ''">
                {{
                  $t(
                    `enums.${column.enum}.${column.decode(
                      propertyValue(scope.row, column.key)
                    )}`
                  )
                }}
              </span>
            </span>
            <span v-if="column.type === 'function'">
              {{ column.get(propertyValue(scope.row, column.key), scope.row) }}
            </span>
            <span v-if="column.type === 'function-i18n'">
              {{
                $t(column.get(propertyValue(scope.row, column.key), scope.row))
              }}
            </span>
            <span
              v-if="column.type === 'slot-collapse-list'"
              class="slot-collapse-list"
            >
              <el-collapse accordion>
                <el-collapse-item
                  :title="$t(column.collapseTitle)"
                  name="slot-collapse-item"
                >
                  <a
                    v-for="(el, collapseIndex) in scope.row[column.collapseKey]"
                    :key="`${column.collapseKey}-${collapseIndex}`"
                    href="#"
                    @click.prevent="$emit('collapseItemClick', scope.row, el)"
                  >
                    <i
                      :class="`state-icon fas fa-${column.collapseItemIcon}`"
                    />
                    {{ el[column.collapseValue] }}
                  </a>
                </el-collapse-item>
              </el-collapse>
            </span>
            <TableActions
              v-else-if="column.type === 'actions'"
              :has-permissions="hasPermissions"
              :configuration="column"
              :row="scope.row"
              @actionClick="$emit($event, scope.row)"
            />
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';
  import lodashGet from 'lodash.get';
  import TableActions from '@/components/TableActions.vue';

  import acl from '@/services/acl';

  export default {
    name: 'SpecificationsTable',
    components: {
      TableActions,
    },
    props: {
      tableColumns: {
        type: Object,
        default: () => {},
      },
      tableKey: {
        type: String,
        required: true,
      },
      bulkDisable: {
        type: Boolean,
        default: false,
      },
      customBulkAction: {
        type: Boolean,
        deafult: false,
      },
      customBulkActionOptions: {
        type: Object,
        default: () => {},
      },
      hasPermissions: {
        type: Boolean,
        default: true,
      },
      columnCondition: {
        type: String,
        default: '',
      },
      height: {
        type: String,
        default: undefined,
      },
      rowKey: {
        type: String,
        default: undefined,
      },
      treeProps: {
        type: Object,
        default: undefined,
      },
      showPagination: { type: Boolean, default: true },
      configurableColumns: { type: Boolean, deafult: false },
      visibleColumns: { type: Array, default: () => [] },
      customRowClass: { type: Function, default: () => '' },
      title: { type: String, default: '' },
    },
    data() {
      return {
        multipleSelection: [],
        showConfigOptions: false,
      };
    },
    computed: {
      ...mapGetters(['tablesStatus', 'enabledColumns']),
      sorting() {
        return this.tablesStatus[this.tableKey].sorting;
      },
      currentPage() {
        return this.tablesStatus[this.tableKey].page;
      },
      perPage() {
        return this.tablesStatus[this.tableKey].perPage;
      },
      count() {
        return this.tablesStatus[this.tableKey].count;
      },
      tableRows() {
        return this.tablesStatus[this.tableKey].rows;
      },
      columns() {
        return Object.keys(this.tableColumns).reduce((columns, key) => {
          if (
            acl.userCanUse(this.tableColumns[key].permissions) &&
            this.checkColumnCondition(this.tableColumns[key])
          ) {
            return { ...columns, [key]: this.tableColumns[key] };
          }
          return columns;
        }, {});
      },
      filteredColumns() {
        const enabledColumns = this.enabledColumns(this.tableKey);
        if (enabledColumns.length) {
          return Object.keys(this.columns).reduce((columns, key) => {
            if (
              enabledColumns.includes(this.columns[key].label) ||
              this.columns[key].type === 'actions'
            ) {
              return { ...columns, [key]: this.columns[key] };
            }
            return columns;
          }, {});
        }
        return this.columns;
      },
      selctableRowsCount() {
        return Object.keys(this.selectableRows).reduce(
          (acc, key) => acc + this.selectableRows[key].length,
          0
        );
      },
      selectableRows() {
        return this.tableRows.reduce(
          (acc, row) => ({
            ...acc,
            [row.groupingId]: row.children.map((r) => r.id),
          }),
          {}
        );
      },
    },
    mounted() {
      this.$emit('reload');
    },
    methods: {
      ...mapActions([
        'updateTableSorting',
        'updateTablePerPage',
        'updateTableCurrentPage',
        'updateColumnSort',
      ]),
      checkColumnCondition(column) {
        return (
          !column.columnCondition ||
          (column.columnCondition &&
            column.columnCondition === this.columnCondition)
        );
      },
      tableCellClassName({ row, column }) {
        if (
          this.columns[column.property] &&
          this.columns[column.property].customClass
        ) {
          const modifier =
            this.columns[column.property].getClassModifier &&
            `table-cell-${this.columns[column.property].key}--${this.columns[
              column.property
            ].getClassModifier(row[column.property])}`;
          return `table-cell-${this.columns[column.property].key} ${
            modifier || ''
          }`;
        }
        return '';
      },
      sortChange(sorting) {
        const { column, ...sortData } = sorting;
        this.$router
          .replace({
            query: {
              ...this.$route.query,
              tableKey: this.tableKey,
              sorting: JSON.stringify({
                ...sortData,
                prop: this.columns[sortData.prop].key,
              }),
            },
          })
          .catch(() => {});
      },
      handleSizeChange(perPage) {
        this.$router
          .replace({
            query: {
              ...this.$route.query,
              tableKey: this.tableKey,
              perPage,
              page: 1,
            },
          })
          .catch(() => {});
      },
      handleCurrentChange(page) {
        this.$router
          .replace({
            query: {
              ...this.$route.query,
              tableKey: this.tableKey,
              page,
            },
          })
          .catch(() => {});
      },
      propertyValue(object, path) {
        return lodashGet(object, path, '');
      },
    },
  };
</script>

<style lang="scss">
  .table-wrapper {
    .table-title {
      font-size: $--font-size-base;
      font-weight: 700;
      color: #a2a4ae;
      margin-bottom: 10px;
    }
    .el-table {
      &__row--level-1 {
        .cell .el-checkbox {
          display: inline-block;
        }
      }
    }
  }
</style>
